import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import Input from '../ui/Input';
import Button from '../ui/Button';
import Icon from '../AppIcon';
import { usePasswordLoginMutation } from '../../features/api/apiSlice';
import { setCredentials } from '../../features/auth/authSlice';
import {
  PORTAL,
  assertPortalAccess,
  buildAuthUserPayload,
} from '../../lib/authPortals';
import { useToast } from '../ui/Toast';

/**
 * Email + password sign-in for user or professional portal.
 * portal: 'customer' | 'professional'
 */
const PasswordLoginForm = ({
  portal = 'customer',
  redirectPath,
  registerPath,
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [passwordLogin, { isLoading }] = usePasswordLoginMutation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const addToast = useToast();

  const defaultRedirect =
    redirectPath ||
    (portal === 'professional' ? '/agent-dashboard' : '/user-dashboard');
  const regPath =
    registerPath || (portal === 'professional' ? '/pro/register' : '/register');

  const validate = () => {
    const errors = {};
    if (!email.trim()) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(email)) errors.email = 'Enter a valid email';
    if (!password) errors.password = 'Password is required';
    else if (password.length < 6) errors.password = 'At least 6 characters';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      const response = await passwordLogin({
        email: email.trim(),
        password,
        portal,
      }).unwrap();

      if (response?.error === true) {
        addToast(response.message || 'Login failed', 'error');
        return;
      }

      const apiUser = response.user || response.data || {};
      const authUser = buildAuthUserPayload({
        apiUser,
        fallback: { email: email.trim() },
      });
      const access = assertPortalAccess(
        authUser.account_role,
        portal === 'professional' ? PORTAL.PROFESSIONAL : PORTAL.CUSTOMER
      );

      if (!access.ok) {
        addToast(access.message, 'error');
        return;
      }

      dispatch(
        setCredentials({
          user: authUser,
          token: response.token,
          customerId: response.customerId || apiUser.id,
        })
      );
      addToast('Login successful!', 'success');
      window.location.href = defaultRedirect || access.redirect || '/user-dashboard';
    } catch (err) {
      addToast(err?.data?.message || err?.message || 'Login failed', 'error');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input
        label="Email"
        type="email"
        autoComplete="email"
        placeholder="you@example.com"
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          if (fieldErrors.email) setFieldErrors((p) => ({ ...p, email: '' }));
        }}
        error={fieldErrors.email}
        required
        disabled={isLoading}
      />
      <div className="relative">
        <Input
          label="Password"
          type={showPassword ? 'text' : 'password'}
          autoComplete="current-password"
          placeholder="Your password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            if (fieldErrors.password) setFieldErrors((p) => ({ ...p, password: '' }));
          }}
          error={fieldErrors.password}
          required
          disabled={isLoading}
          className="pr-12"
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-9 text-muted-foreground hover:text-foreground"
          tabIndex={-1}
        >
          <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
        </button>
      </div>
      <Button type="submit" size="lg" fullWidth loading={isLoading} disabled={isLoading}>
        {isLoading ? 'Signing in…' : 'Sign in with password'}
      </Button>
      <p className="text-center text-sm text-muted-foreground">
        No account?{' '}
        <Link to={regPath} className="text-primary font-medium hover:underline">
          Create one
        </Link>
      </p>
    </form>
  );
};

export default PasswordLoginForm;
